import { supabase } from './database/supabase';
import { mapboxService } from './mapbox';
import { handleApiError, useError } from '@/shared/contexts/ErrorContext';
import { Guest, Location } from '@/types/models/schema';
import { ErrorType } from '@/types/error';
import { ApiResponse } from '@/types/api';

/**
 * Type for error handler function
 */
type ErrorHandler = ReturnType<typeof useError>['addError'];

// Create a mock addError function for service layer
// In a real implementation, this would be passed from the component using the service
const mockAddError: ErrorHandler = (error) => {
  console.error('API Error:', error);
};

/**
 * Interface for map data point
 */
export interface MapDataPoint {
  /** Unique identifier */
  id: string;
  /** Type of data point */
  type: 'guest' | 'team' | 'client';
  /** Location information */
  location: Location;
  /** Name of the entity */
  name: string;
  /** Optional company name */
  company?: string;
  /** Optional job title */
  title?: string;
  /** Optional status information */
  status?: string;
}

/**
 * Interface for map data response
 */
export interface MapDataResponse {
  /** Map data points for guests */
  guests: MapDataPoint[];
  /** Map data points for team members */
  teams: MapDataPoint[];
  /** Map data points for clients */
  clients: MapDataPoint[];
}

/**
 * Interface for location statistics
 */
export interface LocationStatistics {
  /** Total number of locations */
  totalLocations: number;
  /** Number of unique countries */
  countries: number;
  /** Number of guests */
  guestCount: number;
  /** Number of team members */
  teamCount: number;
  /** Number of clients */
  clientCount: number;
}

/**
 * Map service for handling location data and geocoding
 */
export const mapService = {
  /**
   * Get all location data points
   * 
   * @returns Promise resolving to array of map data points
   */
  getAllLocationData: async (): Promise<MapDataPoint[]> => {
    try {
      // Get all guests with locations
      const { data: guests, error } = await supabase
        .from('guests')
        .select('*')
        .not('location', 'is', null);
      
      if (error) throw error;
      
      const mapDataPoints: MapDataPoint[] = [];
      
      // Process guests
      for (const guest of guests as Guest[]) {
        if (guest.location) {
          mapDataPoints.push({
            id: guest.id || `guest-${Math.random().toString(36).substring(2, 9)}`,
            type: 'guest',
            location: guest.location,
            name: guest.name,
            company: guest.company || undefined,
            title: guest.title || undefined,
            status: guest.status
          });
        }
        
        // Process team locations
        if (guest.team_members && Array.isArray(guest.team_members)) {
          guest.team_members.forEach((member, index) => {
            if (member.location) {
              mapDataPoints.push({
                id: `${guest.id}-team-${index}`,
                type: 'team',
                location: member.location,
                name: member.name || `${guest.name}'s Team Member`,
                company: guest.company || undefined
              });
            }
          });
        }
        
        // Process client locations
        if (guest.clients && Array.isArray(guest.clients)) {
          guest.clients.forEach((client, index) => {
            if (client.location) {
              mapDataPoints.push({
                id: `${guest.id}-client-${index}`,
                type: 'client',
                location: client.location,
                name: client.name || `${guest.name}'s Client`,
                company: guest.company || undefined
              });
            }
          });
        }
      }
      
      return mapDataPoints;
    } catch (error) {
      handleApiError(error, mockAddError);
      return []; // Return empty array on error
    }
  },
  
  /**
   * Geocode an address to coordinates
   * 
   * @param address - Address to geocode
   * @returns Promise resolving to location object or null on error
   */
  geocodeAddress: async (address: string): Promise<Location | null> => {
    try {
      const coordinates = await mapboxService.geocode(address);
      
      if (!coordinates) {
        throw new Error('Failed to geocode address');
      }
      
      const [lng, lat] = coordinates;
      
      return {
        lat,
        lng,
        name: address
      };
    } catch (error) {
      handleApiError(error, mockAddError);
      return null;
    }
  },
  
  /**
   * Get location statistics
   * 
   * @returns Promise resolving to ApiResponse with location statistics
   */
  getLocationStatistics: async (): Promise<ApiResponse<LocationStatistics>> => {
    try {
      const dataPoints = await mapService.getAllLocationData();
      
      const countries = new Set<string>();
      let guestCount = 0;
      let teamCount = 0;
      let clientCount = 0;
      
      dataPoints.forEach((point: MapDataPoint) => {
        if (point.location && 'country' in point.location && point.location.country) {
          countries.add(point.location.country as string);
        }
        
        if (point.type === 'guest') guestCount++;
        if (point.type === 'team') teamCount++;
        if (point.type === 'client') clientCount++;
      });
      
      return {
        data: {
          totalLocations: dataPoints.length,
          countries: countries.size,
          guestCount,
          teamCount,
          clientCount
        },
        error: null
      };
    } catch (error) {
      handleApiError(error, mockAddError);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  }
};

export default mapService;
